<template>
  <div class="relative w-full bg-gray-900">
    <!-- Title Strip -->
    <div class="p-4 pb-0">
      <div class="overflow-hidden rounded-lg border border-gray-700">
        <TitleStrip :title="title" :condensed="true" :dark="true" />
      </div>
    </div>

    <!-- Scrollable list -->
    <div class="py-4 px-2 max-h-[50vh] overflow-y-auto">
      <!-- Add with note toggle + New list button -->
      <div
        class="flex justify-between items-center pb-3 mx-4 mb-3 border-b border-white/10">
        <div class="flex items-center gap-3">
          <span class="text-xs font-medium text-white/70">Add with note</span>
          <button
            class="relative w-10 h-5 rounded-full transition-colors"
            :class="addWithNote ? 'bg-indigo-600' : 'bg-gray-600'"
            @click="addWithNote = !addWithNote">
            <span
              class="absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform"
              :class="addWithNote ? 'translate-x-5' : 'translate-x-0'" />
          </button>
        </div>

        <!-- New list button -->
        <button
          class="text-xs font-medium text-indigo-400 hover:text-indigo-300 transition-colors"
          @click="showCreateForm = true">
          + New list
        </button>
      </div>

      <!-- Lists Section (watchlist first, then user lists) -->
      <ListsListPanel :links="false" :lists="regularUserLists">
        <template #action="{ list }">
          <div class="flex gap-x-2 items-center">
            <button
              v-if="quickToggles[list.id]"
              class="flex gap-x-1 justify-center items-center px-2 py-0.5 h-8 text-xs font-bold text-white bg-green-500 rounded-full border border-gray-700 cursor-pointer w-22 group hover:bg-gray-700">
              <fa-icon icon="spinner-third" spin />
            </button>
            <template v-else>
              <button
                v-if="titleIsInListBySlug(list.slug)"
                class="flex gap-x-1 justify-center items-center px-2 py-0.5 h-8 text-xs font-bold text-white bg-green-500 rounded-full border border-gray-700 cursor-pointer w-22 hover:bg-green-700 group"
                @click.prevent="
                  removeFromList(list.id, listItemIdByListId(list.id))
                ">
                <span
                  class="block flex gap-x-1 items-center group-hover:hidden"
                  ><fa-icon :icon="['fas', 'check']" />
                  <span class="">In List</span></span
                >
                <span
                  class="flex hidden gap-x-1 items-center group-hover:block"
                  ><fa-icon :icon="['far', 'circle-minus']" class="mr-1" />
                  <span class="">Remove</span></span
                >
              </button>
              <button
                v-else
                class="flex gap-x-1 justify-center items-center px-2 py-0.5 h-8 text-xs font-bold text-white rounded-full border border-gray-700 cursor-pointer w-22 group hover:bg-gray-700"
                @click.prevent="
                  addWithNote
                    ? addToListWithNote(list.id)
                    : addToList(list.id)
                ">
                <fa-icon :icon="['fa', 'plus']" /><span>Add</span>
              </button>
            </template>
          </div>
        </template>
      </ListsListPanel>
    </div>

    <!-- Note Sheet (opened after add with note) -->
    <NoteSheet
      v-model="showNoteSheet"
      type="list_item"
      :list-item="noteSheetListItem"
      :title="title"
      :title-type="title?.media_type" />

    <!-- Create List Sheet -->
    <Sheet v-model="showCreateForm" title="New List" size="md" content-class="p-0">
      <div class="p-4">
        <div>
          <label class="block text-sm font-medium text-gray-300">
            List Name
          </label>
          <input
            ref="newListNameInput"
            v-model="newListName"
            type="text"
            placeholder="e.g. Weekend Watches"
            class="block py-2.5 px-3 mt-1 w-full text-sm text-gray-200 bg-gray-800 rounded-lg border border-gray-600 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 placeholder-gray-500"
            @keydown.enter="createAndAddToList" />
        </div>

        <div class="mt-4">
          <label class="block text-sm font-medium text-gray-300">
            Description
            <span class="text-gray-500 font-normal">(optional)</span>
          </label>
          <textarea
            v-model="newListDescription"
            rows="3"
            maxlength="500"
            placeholder="What is this list about?"
            class="block py-2.5 px-3 mt-1 w-full text-sm text-gray-200 bg-gray-800 rounded-lg border border-gray-600 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 placeholder-gray-500 resize-none" />
          <p class="mt-1 text-xs text-gray-500">
            {{ newListDescription.length }}/500
          </p>
        </div>

        <!-- Error message -->
        <div
          v-if="createError"
          class="p-3 mt-4 text-sm rounded-lg cursor-pointer bg-red-500/20 border border-red-500/50 text-red-200"
          @click="createError = null">
          <div class="flex items-center gap-2">
            <fa-icon icon="exclamation-circle" />
            <span>{{ createError }}</span>
          </div>
          <p class="text-xs text-red-300/70 mt-1">Tap to dismiss</p>
        </div>

        <!-- Action buttons -->
        <div class="pt-4 mt-4 border-t border-white/10">
          <div class="flex gap-2">
            <button
              type="button"
              class="flex-1 py-3 text-sm font-medium text-gray-300 bg-gray-800 rounded-lg hover:bg-gray-700 transition-colors"
              @click="cancelCreate">
              Cancel
            </button>
            <button
              type="button"
              :disabled="isCreating || !newListName.trim()"
              class="flex-[2] flex items-center justify-center gap-2 py-3 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              @click="createAndAddToList">
              <template v-if="isCreating">
                Creating...
                <fa-icon icon="spinner-third" class="fa-spin" />
              </template>
              <template v-else>Create & Add</template>
            </button>
          </div>
        </div>
      </div>
    </Sheet>
  </div>
</template>

<script setup>
import { computed, ref, watch, onMounted, nextTick } from 'vue'
import { useStoreLists } from '@/stores/storeLists'
import { useStoreTitlesUser } from '@/stores/storeTitlesUser'
import { useHapticFeedback } from '@/composables/useHapticFeedback'
import ListsListPanel from '@/components/ListsListPanel'
import TitleStrip from '@/components/TitleStrip.vue'
import NoteSheet from '@/components/Sheets/NoteSheet.vue'
import { Sheet } from '@/components/Options'
import { useTitleActions } from '@/use/useTitleActions'

const STORAGE_KEY_ADD_WITH_NOTE = 'spectra_add_to_list_with_note'

const props = defineProps({
  title: {
    type: Object,
    default: null
  },
  titleInfo: {
    type: Object,
    default: null
  },
  userTitleInfo: {
    type: Object,
    default: null
  },
  metadata: {
    type: Object,
    default: null
  }
})

const emit = defineEmits(['close'])

const storeLists = useStoreLists()
const storeTitlesUser = useStoreTitlesUser()
const haptics = useHapticFeedback()
const quickToggles = ref({})

// Note sheet state
const showNoteSheet = ref(false)
const noteSheetListItem = ref(null)

// Create new list form state
const showCreateForm = ref(false)
const newListName = ref('')
const newListDescription = ref('')
const isCreating = ref(false)
const createError = ref(null)
const newListNameInput = ref(null)

// Add with note toggle - persisted in localStorage
const addWithNote = ref(false)

// Load preference from localStorage on mount
onMounted(() => {
  const saved = localStorage.getItem(STORAGE_KEY_ADD_WITH_NOTE)
  if (saved !== null) {
    addWithNote.value = saved === 'true'
  }

  // Fetch data when mounted
  if (props.title) {
    fetchData()
  }
})

// Persist preference when changed
watch(addWithNote, (newValue) => {
  localStorage.setItem(STORAGE_KEY_ADD_WITH_NOTE, String(newValue))
})

const actionProps = computed(() => {
  if (!props.title) return { title: null, titleInfo: null, userTitleInfo: null }

  const storeUserTitleInfo = storeTitlesUser.getTitleByTitleId({
    id: props.title.id,
    type: props.title.media_type
  })

  return {
    title: props.title,
    titleInfo: props.titleInfo,
    userTitleInfo: storeUserTitleInfo || props.userTitleInfo
  }
})

const { quickStateWatchlist, usesLogSystem } = useTitleActions(actionProps)

const titleDbId = computed(() => {
  if (!props.title) return null

  const storeUserTitleInfo = storeTitlesUser.getTitleByTitleId({
    id: props.title.id,
    type: props.title.media_type
  })

  return (
    props.titleInfo?.id ||
    storeUserTitleInfo?.title_id ||
    props.userTitleInfo?.title_id
  )
})

const listItemIdByListId = (listId) => {
  if (!listId || !props.title) return null
  const titleId = titleDbId.value
  const tmdbTitleId = props.title.id

  let items = storeLists.items.filter(
    (item) => item.list_id === listId && item.title_id === titleId
  )

  if (items.length === 0) {
    items = storeLists.items.filter((item) => {
      if (item.list_id !== listId) return false
      if (item.titles && item.titles.tmdb_id === tmdbTitleId) return true
      return false
    })
  }

  return items.length > 0 ? items[0].id : null
}

const titleIsInListBySlug = (slug) => {
  if (!props.title) return false
  const list = storeLists.getUserListBySlug(slug)
  if (!list) return false

  const listId = list.id
  const tmdbTitleId = props.title.id
  const dbTitleId = titleDbId.value

  const items = storeLists.items.filter((item) => {
    if (item.list_id !== listId) return false
    if (dbTitleId && item.title_id === dbTitleId) return true
    if (item.titles && item.titles.tmdb_id === tmdbTitleId) return true
    return false
  })

  return items.length > 0
}

const quickToggle = (listId, value) => {
  if (listId === undefined) return
  quickToggles.value = {
    ...quickToggles.value,
    [listId]: value
  }
}

const watchlistSystemList = computed(() => {
  return storeLists.getSystemListByType('watchlist')
})

const regularUserLists = computed(() => {
  // Get user's editable lists (owned + collaborative lists user is a member of)
  // Includes watchlist system list, excludes seen, auto, and stub lists
  const lists = storeLists.getUserLists.filter((list) => {
    // Exclude seen system list
    if (list.is_system && list.system_type === 'seen') return false
    // Exclude auto-generated and stub lists
    if (list.is_auto || list.is_stub) return false
    return true
  })

  // Sort: watchlist first, then by updated_at descending
  return lists.sort((a, b) => {
    // Watchlist always first
    if (a.is_system && a.system_type === 'watchlist') return -1
    if (b.is_system && b.system_type === 'watchlist') return 1

    // Then sort by updated_at descending
    const dateA = new Date(a.updated_at || a.created_at)
    const dateB = new Date(b.updated_at || b.created_at)
    return dateB - dateA
  })
})

const addToList = async (listId) => {
  if (!props.title) return
  quickToggle(listId, true)
  try {
    // addToList now returns list_item data and updates store optimistically
    await storeLists.addToList(
      listId,
      {
        titleId: props.title.id,
        titleType: props.title.media_type
      },
      props.metadata
    )

    // Small delay for UI transition (keeping existing behavior)
    await new Promise((resolve) => setTimeout(resolve, 100))

    if (watchlistSystemList.value && listId === watchlistSystemList.value.id) {
      quickStateWatchlist.value = true

      // Also update log system so the title state updates everywhere
      if (usesLogSystem.value) {
        const { useStoreTitleLogs } = await import('@/stores/storeTitleLogs')
        const storeTitleLogs = useStoreTitleLogs()
        await storeTitleLogs.addToWatchlist({
          id: props.title.id,
          type: props.title.media_type
        })
      }
    }
  } finally {
    quickToggle(listId, false)
  }
}

const addToListWithNote = async (listId) => {
  if (!props.title) return
  quickToggle(listId, true)

  try {
    // Add to list - now returns list_item data directly
    const addResult = await storeLists.addToList(
      listId,
      {
        titleId: props.title.id,
        titleType: props.title.media_type
      },
      props.metadata
    )

    // Use the returned list_item directly (no need to wait/refetch)
    let listItem = addResult?.list_item || null

    // Fallback: find in store if not returned (shouldn't happen)
    if (!listItem) {
      // Wait briefly for store update
      await new Promise((resolve) => setTimeout(resolve, 1000))
      const listItemId = listItemIdByListId(listId)
      if (listItemId) {
        listItem = storeLists.items.find((item) => item.id === listItemId)
      }
    }

    // Open note sheet
    if (listItem) {
      noteSheetListItem.value = listItem
      showNoteSheet.value = true
    }
  } finally {
    quickToggle(listId, false)
  }
}

const removeFromList = async (listId, itemId) => {
  if (!props.title) return
  quickToggle(listId, true)

  try {
    // removeItemFromList already updates store optimistically
    await storeLists.removeItemFromList(listId, itemId)

    // Small delay for UI transition (keeping existing behavior)
    await new Promise((resolve) => setTimeout(resolve, 100))

    if (watchlistSystemList.value && listId === watchlistSystemList.value.id) {
      quickStateWatchlist.value = false

      // Also remove from log system so the title state updates everywhere
      if (usesLogSystem.value) {
        const { useStoreTitleLogs } = await import('@/stores/storeTitleLogs')
        const storeTitleLogs = useStoreTitleLogs()
        await storeTitleLogs.removeFromState(
          { id: props.title.id, type: props.title.media_type },
          'watchlist'
        )
      }
    }
  } finally {
    quickToggle(listId, false)
  }
}

const fetchData = async () => {
  if (!props.title) return
  try {
    await Promise.all([
      storeLists.fetchItemsByTitle({
        id: props.title.id,
        type: props.title.media_type
      }),
      refreshUserTitleData()
    ])
  } catch (error) {
    console.warn('Error fetching data:', error)
  }
}

const refreshUserTitleData = async () => {
  if (!props.title) return
  try {
    await storeTitlesUser.fetchInfoByTitle(
      {
        id: props.title.id,
        type: props.title.media_type
      },
      true
    )
  } catch (error) {
    console.warn('Error refreshing user title data:', error)
  }
}

// Create new list form functions
const cancelCreate = () => {
  haptics.light()
  showCreateForm.value = false
  newListName.value = ''
  newListDescription.value = ''
  createError.value = null
}

const createAndAddToList = async () => {
  if (!newListName.value.trim() || isCreating.value || !props.title) return

  haptics.medium()
  isCreating.value = true
  createError.value = null

  try {
    // Create the new list
    const res = await storeLists.addList({
      title: newListName.value.trim(),
      description: newListDescription.value.trim(),
      list_type: 'custom'
    })

    const newList = res.data?.list
    if (!newList?.id) {
      throw new Error('Failed to create list')
    }

    // Add the title to the newly created list
    await storeLists.addToList(
      newList.id,
      {
        titleId: props.title.id,
        titleType: props.title.media_type
      },
      props.metadata
    )

    haptics.success()

    // Reset form and close sheet
    showCreateForm.value = false
    newListName.value = ''
    newListDescription.value = ''

    // If add with note is enabled, open note sheet
    if (addWithNote.value) {
      // Wait briefly for store update
      await new Promise((resolve) => setTimeout(resolve, 500))
      const listItemId = listItemIdByListId(newList.id)
      if (listItemId) {
        const listItem = storeLists.items.find((item) => item.id === listItemId)
        if (listItem) {
          noteSheetListItem.value = listItem
          showNoteSheet.value = true
        }
      }
    }
  } catch (error) {
    console.error('Error creating list and adding title:', error)
    createError.value = error?.message || 'Failed to create list. Please try again.'
    haptics.error()
  } finally {
    isCreating.value = false
  }
}

// Focus input when sheet opens (slight delay for sheet animation)
watch(showCreateForm, async (isOpen) => {
  if (isOpen) {
    createError.value = null
    await nextTick()
    setTimeout(() => newListNameInput.value?.focus(), 300)
  }
})
</script>
